import { log } from '@src/Log.init';
import React, { useState } from 'react';
import Spacer from 'react-spacer';

// @mui/material components
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { makeStyles } from '@mui/styles';
// @mui/icons-material
import ReferenceIcon from '@mui/icons-material/Announcement';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';

// core components
import { selectLocale } from '@features/session/sessionSlice';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardIcon from 'components/Card/CardIcon.js';
import Button from 'components/CustomButtons/Button.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Table from 'components/Table/Table.js';

import ReferenceListEntry from './ReferenceListEntry.js';

// style for this view
import styles from 'assets/jss/material-dashboard-pro-react/views/clientEditStyle.js';

import {
  ThemedPrimaryBtn,
  ThemedSecondaryBtn,
} from '@themes/defaultThemeProps';
import { selectViewIsDirty, setViewIsDirty } from 'features/ux/uxSlice';
import { useDispatch, useSelector } from 'react-redux';

import {
  useGetReferenceListsQuery,
  useUpdateReferenceListMutation,
} from 'api/reference-list.api.js';
import { useAvailableLanguages } from 'hooks/useAvailableLanguages';

const useStyles = makeStyles(styles);

export default function ReferenceLists() {
  const classes = useStyles();
  const { data: referenceLists } = useGetReferenceListsQuery();
  const supportedLanguages = useAvailableLanguages();
  const viewIsDirty = useSelector(selectViewIsDirty);
  const dispatch = useDispatch();

  const [editEntry, setEditEntry] = React.useState(false);
  const [deleteEntry, setDeleteEntry] = React.useState(false);
  const [addToList, setAddToList] = React.useState(false);
  const [defaultBehavior, setDefaultBehavior] = useState('first');
  const [defaultSelection, setDefaultSelection] = useState(0);
  const [currentList, setCurrentList] = useState(null);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [expanded, setExpanded] = React.useState(false);

  const [updateReferenceList] = useUpdateReferenceListMutation();

  const handleExpansionChange = (panel, list) => (event, isExpanded) => {
    setCurrentList(list);
    setDefaultBehavior(list.default_type);
    setDefaultSelection(list.default_value);
    setExpanded(isExpanded ? panel : false);
    dispatch(setViewIsDirty(false));
  };

  const getLanguages = () => {
    return supportedLanguages?.join(', ');
  };

  const addListEntryHandler = (list) => {
    setCurrentList(list);
    setAddToList(true);
  };

  const handleEditEntry = (list, entry, index) => {
    setCurrentList(list);
    setCurrentEntry(entry);
    setCurrentIndex(index);
    setEditEntry(true);
  };

  const handleDeleteEntry = (list, entry, index) => {
    setCurrentList(list);
    setCurrentEntry(entry);
    setCurrentIndex(index);
    setDeleteEntry(true);
  };

  const deleteCurrentEntry = () => {
    var updatedList = { ...currentList };
    var updatedLabelList = [...currentList.list];
    updatedLabelList.splice(currentIndex, 1);
    updatedList.list = updatedLabelList;
    updateReferenceList(updatedList)
      .unwrap()
      .catch((error) => {
        log.debug({
          message_type: 'ReferenceLists',
          message: 'Error updating reference list',
          meta_data: error,
        });
      });
    setDeleteEntry(false);
  };

  const handleCloseEntry = () => {
    setEditEntry(false);
    setAddToList(false);
  };

  const defaultBehaviorHandler = (event, list) => {
    setCurrentList(list);
    const newValue = event.target.value;

    if (newValue !== defaultBehavior) {
      dispatch(setViewIsDirty(true));
    }

    setDefaultBehavior(newValue);
    if (newValue === 'default')
      if (defaultSelection === '0') setDefaultSelection('1');
    if (newValue === 'first') setDefaultSelection('1');
    if (newValue === 'none') setDefaultSelection('0');
  };

  const saveDefaultHandler = (list) => {
    setCurrentList(list);
    var updatedList = { ...list };
    updatedList.default_type = defaultBehavior;
    updatedList.default_value = defaultSelection;
    updateReferenceList(updatedList)
      .unwrap()
      .catch((error) => {
        log.debug({
          message_type: 'ReferenceLists',
          message: 'Error updating reference list',
          meta_data: error,
        });
      });
    dispatch(setViewIsDirty(false));
  };

  const defaultSelectionHandler = (event) => {
    const newValue = event.target.value.toString();

    if (newValue !== defaultSelection) {
      dispatch(setViewIsDirty(true));
    }

    setDefaultSelection(newValue);
  };

  const actionButton = (Icon, color, handler, key) => {
    return (
      <Button
        key={key}
        color={color}
        simple
        className={classes.actionButton}
        onClick={() => handler(key)}
      >
        <Icon className={classes.icon} />
      </Button>
    );
  };

  // create the data list of entries for a specific reference list for display in a table
  const listEntries = (list) => {
    const getLabel = (labels) => {
      const locale = useSelector(selectLocale);
      const labelLocale = labels.findLast(
        (label) => label?.language === locale
      );
      const firstLabel = labels.find((label) => label?.label?.trim() !== '');
      return labelLocale === undefined || labelLocale?.label?.trim() === ''
        ? firstLabel
        : labelLocale;
    };
    const tableList = list.list.map((entry, key) => {
      let entries = [];
      let actionButtons = [];
      const result = getLabel(entry.entry_label);
      entries.push(entry.entry_code);
      entries.push(result?.label);
      actionButtons.push(
        actionButton(
          Edit,
          'warning',
          () => {
            handleEditEntry(list, entry, key);
          },
          key
        )
      );
      actionButtons.push(
        actionButton(
          Delete,
          'warning',
          () => {
            handleDeleteEntry(list, entry, key);
          },
          key + 1
        )
      );
      entries.push(actionButtons);
      return entries;
    });
    return tableList;
  };

  // create the list of default list options for selection by the user
  const defaultOptions = (list) => {
    const optionsList = list.list.map((entry, key) => (
      <MenuItem key={list.name + '-default-' + (key + 1)} value={key + 1}>
        {entry.entry_label[0].label}
      </MenuItem>
    ));
    optionsList.splice(
      0,
      0,
      <MenuItem key='default-0' value={0}>
        -none-
      </MenuItem>
    );
    return optionsList;
  };

  const ReferenceLists = () => {
    return [
      referenceLists.map((entry, key) => {
        const tData = listEntries(entry);
        const defaultOptionsList = defaultOptions(entry);
        return (
          <Accordion
            expanded={expanded === 'panel' + key}
            onChange={handleExpansionChange('panel' + key, entry)}
            key={key}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls='panel1a-content'
              id={entry.name}
            >
              <Typography className={classes.heading}>
                {entry.name.toUpperCase()} (
                {entry.source === 'base' ? 'Default' : 'Custom'})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <GridContainer>
                <GridItem xs={12}>
                  <Button
                    color='primary'
                    onClick={() => {
                      addListEntryHandler(entry);
                    }}
                    className={classes.leftButton}
                  >
                    + Add List Entry
                  </Button>
                </GridItem>
                <GridItem xs={8}>
                  <Table
                    tableHead={['Code', 'Label', 'Actions']}
                    tableData={tData}
                  />
                </GridItem>
                <GridItem xs={4}>
                  <GridContainer>
                    <GridItem xs={12}>
                      <Spacer height='36px' />
                    </GridItem>
                    <GridItem xs={12}>
                      <FormControl
                        variant='standard'
                        className={classes.formControl}
                      >
                        <InputLabel
                          id='behavior-select-label'
                          className={classes.inputLabel}
                        >
                          Default Behavior
                        </InputLabel>
                        <Select
                          variant='standard'
                          className={classes.select}
                          labelId='behavior-select-label'
                          id={'behavior-select-' + entry.name}
                          value={defaultBehavior}
                          onChange={(event) =>
                            defaultBehaviorHandler(event, entry)
                          }
                        >
                          <MenuItem key='default' value='default'>
                            Use Selected Default
                          </MenuItem>
                          <MenuItem key='first' value='first'>
                            Default to First in List
                          </MenuItem>
                          <MenuItem key='none' value='none'>
                            No Default - Require Selection
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </GridItem>
                    <GridItem xs={12}>
                      <Spacer height='24px' />
                    </GridItem>
                    <GridItem xs={12}>
                      <FormControl
                        variant='standard'
                        className={classes.formControl}
                      >
                        <InputLabel
                          id='default-select-label'
                          className={classes.inputLabel}
                        >
                          Default Selection
                        </InputLabel>
                        <Select
                          variant='standard'
                          className={classes.select}
                          labelId='default-select-label'
                          id={'default-select-' + entry.name}
                          value={defaultSelection}
                          onChange={(event) =>
                            defaultSelectionHandler(event, entry)
                          }
                        >
                          {defaultOptionsList}
                        </Select>
                      </FormControl>
                    </GridItem>
                    <GridItem xs={12}>
                      <Spacer height='24px' />
                    </GridItem>
                    <GridItem xs={12}>
                      <Button
                        color='primary'
                        disabled={!viewIsDirty}
                        onClick={() => {
                          saveDefaultHandler(entry);
                        }}
                        className={classes.leftButton}
                      >
                        Save Changes
                      </Button>
                    </GridItem>
                  </GridContainer>
                </GridItem>
              </GridContainer>
            </AccordionDetails>
          </Accordion>
        );
      }),
    ];
  };

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color='primary' icon>
            <CardIcon color='primary'>
              <ReferenceIcon />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Default Reference Lists</h4>
          </CardHeader>
          <CardBody>
            <Spacer height='24px' />
            <h5>
              Supported languages:{' '}
              {supportedLanguages?.length > 0 ? getLanguages() : ''}
            </h5>
            <ReferenceLists />
          </CardBody>
        </Card>
      </GridItem>
      <Dialog
        open={deleteEntry}
        onClose={() => setDeleteEntry(false)}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>
          Delete{' '}
          {currentEntry
            ? currentEntry.entry_code + '-' + currentEntry.entry_label[0].label
            : ''}{' '}
          from {currentList ? currentList.name.toUpperCase() : ''} list?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            Deleting this entry cannot be undone!
            <br />
            Press Delete to confirm, or Cancel to cancel.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <ThemedSecondaryBtn onClick={() => setDeleteEntry(false)}>
            Cancel
          </ThemedSecondaryBtn>
          <ThemedPrimaryBtn
            onClick={() => deleteCurrentEntry()}
            color='primary'
            autoFocus
          >
            Delete
          </ThemedPrimaryBtn>
        </DialogActions>
      </Dialog>
      {editEntry === true ? (
        <ReferenceListEntry
          open={editEntry}
          base={false}
          handleClose={handleCloseEntry}
          list={currentList}
          entry={currentEntry}
          index={currentIndex}
        />
      ) : null}
      {addToList === true ? (
        <ReferenceListEntry
          open={addToList}
          base={false}
          new
          handleClose={handleCloseEntry}
          list={currentList}
        />
      ) : null}
    </GridContainer>
  );
}
